import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../auth';
import { TipReceivedToast } from './TipReceivedToast';
import { KickRewardToast } from './KickRewardToast';
import { TipSentToast } from './TipSentToast';
import { TIP_TOAST_OPTIONS } from './TipToastFrame';
import { useTipNotificationSound } from './useTipNotificationSound';
import { useRewardClaimNotifications } from './useRewardClaimNotifications';
import { showTestAlert } from './showTestAlert';
import './notifications.css';

const emptyFeed = { notifications: [], unread_count: 0 };
const NotificationContext = createContext({ feed: emptyFeed, error: '', refresh: () => {}, mark: () => {} });
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('gv_token')}` });

const NotificationSession = ({ userId, children }) => {
  const [feed, setFeed] = useState(emptyFeed), [error, setError] = useState('');
  const fetching = useRef(false), shown = useRef(new Set()), baseline = useRef(false), toastIds = useRef([]);
  const mounted = useRef(true);
  const sessionStarted = useRef(Date.now());
  const api = `${process.env.REACT_APP_BACKEND_URL}/api/notifications`;
  const storageKey = `gv_tip_alerts_seen_${userId}`;
  const playTipSound = useTipNotificationSound();
  useRewardClaimNotifications(userId, mounted, toastIds, playTipSound);
  const refresh = useCallback(async (signal) => {
    if (fetching.current || !mounted.current) return;
    fetching.current = true;
    try {
      const { data } = await axios.get(api, { headers: headers(), signal });
      if (signal?.aborted || !mounted.current) return;
      const tips = data.notifications.filter((notice) => ['tip', 'kick_reward', 'kicklet_reward'].includes(notice.kind));
      {
        const arrivals = tips.filter((notice) => !shown.current.has(notice.id) && !notice.read && (baseline.current || Date.parse(notice.created_at) >= sessionStarted.current)).reverse();
        for (const notice of arrivals) {
          const id = `received-${notice.id}`;
          toast.custom(() => notice.kind === 'tip' ? <TipReceivedToast notice={notice} dismiss={() => toast.dismiss(id)} /> : <KickRewardToast notice={notice} onDismiss={() => toast.dismiss(id)} />, { ...TIP_TOAST_OPTIONS, id });
          toastIds.current.push(id);
        }
        if (arrivals.length) {
          playTipSound(arrivals[arrivals.length - 1].id);
          window.dispatchEvent(new CustomEvent('gv-wallet-updated'));
        }
      }
      tips.forEach((notice) => shown.current.add(notice.id));
      const tests = data.notifications.filter((notice) => notice.kind === 'test_alert' && notice.test_only);
      for (const notice of [...tests].reverse()) {
        if (!document.hidden && !shown.current.has(notice.id) && !notice.read) {
          showTestAlert(notice, toastIds, playTipSound);
          shown.current.add(notice.id);
          // Acknowledge test delivery so old simulations don't replay next login.
          axios.post(`${api}/read`, { notification_id: notice.id }, { headers: headers() }).catch(() => {});
        }
        if (notice.read) shown.current.add(notice.id);
      }
      baseline.current = true;
      try { sessionStorage.setItem(storageKey, JSON.stringify([...shown.current].slice(-500))); } catch { /* In-memory dedup remains available. */ }
      setFeed(data); setError('');
    } catch (failure) { if (!axios.isCancel(failure)) setError('Notifications are temporarily unavailable.'); }
    finally { fetching.current = false; }
  }, [api, storageKey, playTipSound]);
  useEffect(() => {
    mounted.current = true;
    const abort = new AbortController();
    try { const saved = sessionStorage.getItem(storageKey); shown.current = new Set(saved ? JSON.parse(saved) : []); baseline.current = !!saved; } catch { baseline.current = false; }
    // StrictMode's discarded effect must not reserve the in-flight refresh slot.
    queueMicrotask(() => { if (!abort.signal.aborted) refresh(abort.signal); });
    const timer = setInterval(() => { if (!document.hidden) refresh(abort.signal); }, 3500);
    const focused = () => refresh(abort.signal);
    const visible = () => { if (!document.hidden) refresh(abort.signal); };
    window.addEventListener('focus', focused);
    window.addEventListener('gv-notifications-refresh', focused);
    document.addEventListener('visibilitychange', visible);
    const currentToastIds = toastIds.current;
    return () => { mounted.current = false; abort.abort(); clearInterval(timer); window.removeEventListener('focus', focused); window.removeEventListener('gv-notifications-refresh', focused); document.removeEventListener('visibilitychange', visible); currentToastIds.forEach((id) => toast.dismiss(id)); };
  }, [refresh, storageKey]);
  useEffect(() => {
    const sentKey = `gv_sent_tip_alerts_${userId}`;
    let completed = new Set();
    try { completed = new Set(JSON.parse(sessionStorage.getItem(sentKey)) || []); } catch { /* In-memory dedup remains available. */ }
    const sent = (event) => {
      const receipt = event.detail?.receipt;
      if (!mounted.current || event.detail?.user_id !== userId || receipt?.status !== 'completed' || !receipt.request_id || completed.has(receipt.request_id)) return;
      completed.add(receipt.request_id);
      try { sessionStorage.setItem(sentKey, JSON.stringify([...completed].slice(-500))); } catch { /* Keep this session's in-memory dedup. */ }
      const id = `sent-${userId}-${receipt.request_id}`;
      toast.custom(() => <TipSentToast receipt={receipt} dismiss={() => toast.dismiss(id)} />, { ...TIP_TOAST_OPTIONS, id });
      toastIds.current.push(id);
      playTipSound(id);
    };
    window.addEventListener('gv-tip-sent', sent);
    return () => window.removeEventListener('gv-tip-sent', sent);
  }, [userId, playTipSound]);
  const mark = async (id = null) => {
    try { await axios.post(`${api}/${id ? 'read' : 'read-all'}`, id ? { notification_id: id } : {}, { headers: headers() }); await refresh(); }
    catch { setError('Unable to mark notifications as read.'); }
  };
  return <NotificationContext.Provider value={{ feed, error, refresh, mark }}>{children}</NotificationContext.Provider>;
};

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  return user ? <NotificationSession key={user.id} userId={user.id}>{children}</NotificationSession> : <NotificationContext.Provider value={{ feed: emptyFeed, error: '', refresh: () => {}, mark: () => {} }}>{children}</NotificationContext.Provider>;
};
export const useNotifications = () => useContext(NotificationContext);